import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grid, TextField, InputAdornment, CircularProgress, Alert, Button, MenuItem } from '@mui/material';
import DateRangeDropdown from '../components/DateRangeDropdown';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import PendingActionsRoundedIcon from '@mui/icons-material/PendingActionsRounded';
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AttStatCard from '../components/LeaveAttendanceComps/AttStatCard';
import ExpenseDetail from '../components/ExpenseComps/ExpenseDetail';
import { balanceTone, balanceWord } from '../components/ExpenseComps/PaymentLedger';
import { card, th, td, field, EmptyState } from '../components/uiKit';
import { PRIMARY } from '../theme';
import SearchSelect from '../components/SearchSelect';
import http, { apiErrorMessage } from '../Api/http';
import { GetShootPaymentHistoryOverview } from '../Api/Api';
import { useCrumbActionsSetter } from '../utils/crumbActions';
import { inr, fmtDate, parseApiDate } from '../utils/format';

const GRID = '1.4fr 90px 1fr 1fr 1fr 1fr 130px';
const num = (v) => Number(v) || 0;
const toDate = (v) => { const d = parseApiDate(v); return d ? fmtDate(d) : '—'; };

// One row per employee with any shoot activity, straight from GetShootPaymentHistoryOverview.
// Clicking an employee opens their payment history (date by date) on this same tab.
export default function PaymentHistoryPage({ data, config }) {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const employee = params.get('employee');
    const [q, setQ] = useState('');
    const [from, setFrom] = useState('');
    const [to, setTo] = useState('');
    const [emp, setEmp] = useState('all');
    const [stype, setStype] = useState('all');
    const shootTypes = config.shootTypes || [];
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const load = useCallback(async (search) => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetShootPaymentHistoryOverview, { params: { search: search || undefined } });
            if (body?.error) throw new Error(body.message || 'Could not load the payment history.');
            setOverview(body?.data ?? body ?? {});
            setError('');
        } catch (err) {
            if (err?.response?.status === 404) { setOverview({}); setError(''); } else setError(err?.response ? apiErrorMessage(err, 'Could not load the payment history.') : (err.message || 'Could not load the payment history.'));
        } finally {
            setLoading(false);
        }
    }, []);

    // Search runs on the server; wait a beat after typing so every keystroke is not a request. Reload
    // after the shoot data changes (an expense or payment added/removed elsewhere) as well.
    useEffect(() => {
        if (employee) return undefined;
        const t = setTimeout(() => load(q.trim()), q ? 300 : 0);
        return () => clearTimeout(t);
    }, [q, employee, load, data.rows.length, data.payments.length]);

    // Every employee seen in the data, for the employee filter.
    const everyone = useMemo(() => {
        const names = new Map(); // code -> name
        (overview?.employees || []).forEach((p) => { if (p.employeeCode) names.set(p.employeeCode, p.employeeName || p.employeeCode); });
        data.rows.forEach((r) => (r.empStats || []).forEach((e) => { if (e.code && !names.has(e.code)) names.set(e.code, e.name || e.code); }));
        data.payments.forEach((pm) => { if (pm.employeeCode && !names.has(pm.employeeCode)) names.set(pm.employeeCode, pm.employeeName || pm.employeeCode); });
        return names;
    }, [overview, data.rows, data.payments]);

    // The API has no date or shoot-type filter, so while either is set the figures are recomputed here from
    // the shoot expenses and payments already loaded. A payment follows its shoot name's type.
    const dateTypeActive = Boolean(from || to || stype !== 'all');
    const local = useMemo(() => {
        if (!dateTypeActive) return null;
        const inRange = (d) => !((from && d < from) || (to && d > to));
        const rows = data.rows.filter((x) => inRange(x.date) && (stype === 'all' || x.shootType === stype));
        const shootsOfType = new Set(data.rows.filter((x) => x.shootType === stype).map((x) => String(x.project || '').trim().toLowerCase()));
        const pays = data.payments.filter((x) => inRange(x.date) && (stype === 'all' || shootsOfType.has(String(x.shoot || '').trim().toLowerCase())));
        const sum = (list, fn) => list.reduce((n, r) => n + fn(r), 0);
        const people = [...everyone.entries()].map(([code, name]) => {
            const mine = rows.filter((r) => (r.employeeCodes || []).includes(code));
            const paid = pays.filter((p) => p.employeeCode === code);
            const total = sum(mine, (r) => r.amount);
            const prepaid = sum(paid.filter((p) => p.type === 'Prepaid'), (p) => p.amount);
            const postpaid = sum(paid.filter((p) => p.type === 'Postpaid'), (p) => p.amount);
            const balance = prepaid + postpaid - total;
            const dates = [...mine.map((r) => r.date), ...paid.map((p) => p.date)].filter(Boolean).sort();
            return { employeeCode: code, employeeName: name, entries: mine.length, total, prepaid, postpaid, balance, balanceStatus: balance < 0 ? 'due' : balance > 0 ? 'advance' : 'settled', lastActivity: dates[dates.length - 1] || null };
        }).filter((p) => p.entries || p.prepaid || p.postpaid);
        return { people };
    }, [dateTypeActive, from, to, stype, data.rows, data.payments, everyone]);

    // Search, the filters and the dropdowns sit in the breadcrumb row (hidden while an employee's page is open).
    const setCrumbActions = useCrumbActionsSetter();
    useEffect(() => {
        if (!setCrumbActions || employee) return undefined;
        const tall = { ...field['& .MuiOutlinedInput-root'], height: 42, bgcolor: '#fff' };
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                    size="small" placeholder="Search employee…" value={q} onChange={(e) => setQ(e.target.value)}
                    sx={{ ...field, width: { xs: '100%', sm: 260 }, '& .MuiOutlinedInput-root': tall }}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18 }} /></InputAdornment> } }}
                />
                <DateRangeDropdown from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} placeholder="All dates" title="Date range" height={42} />
                <Box sx={{ width: 200 }}>
                    <SearchSelect
                        value={emp} onChange={(e) => setEmp(e.target.value)} searchPlaceholder="Search employees…"
                        sx={{ ...field, '& .MuiOutlinedInput-root': tall }}
                        options={[{ value: 'all', label: 'All employees' }, ...[...everyone.entries()].map(([code, name]) => ({ value: code, label: name }))]}
                    />
                </Box>
                {shootTypes.length > 0 && (
                    <TextField select size="small" value={stype} onChange={(e) => setStype(e.target.value)} sx={{ ...field, width: 150, '& .MuiOutlinedInput-root': tall }}>
                        <MenuItem value="all">All shoot types</MenuItem>
                        {shootTypes.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                )}
            </Box>,
        );
        return () => setCrumbActions(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setCrumbActions, q, from, to, emp, stype, employee, everyone]);

    const open = (code) => setParams({ tab: 'payments', employee: code });
    const basePeople = local ? local.people : (Array.isArray(overview?.employees) ? overview.employees : []);
    // The employee filter and, while the local figures are in use, the search apply here.
    const people = basePeople.filter((p) => (emp === 'all' || p.employeeCode === emp)
        && (!local || !q.trim() || `${p.employeeName} ${p.employeeCode}`.toLowerCase().includes(q.trim().toLowerCase())));
    const nameOf = (code) => people.find((p) => p.employeeCode === code)?.employeeName
        || data.rows.flatMap((r) => r.empStats || []).find((e) => e.code === code)?.name || code;
    const statSize = { xs: 12, sm: 6, md: 3 };

    // An employee's page stays on this tab; the back arrow returns to the list.
    if (employee) {
        return (
            <ExpenseDetail
                config={config} kind="employee" value={employee} title={nameOf(employee)}
                rows={data.rows} columns={[]}
                onOpenEntry={(id) => navigate(`/dashboard/expenses?tab=shoot&entry=${id}`)}
                onRemovePayment={data.removePayment} onRemoveExpense={data.removeExpense}
                refreshKey={data.rows.length + data.payments.length}
                onBack={() => setParams({ tab: 'payments' })}
            />
        );
    }

    // Cards: the API's totals when nothing but search is set, otherwise the sum of the rows shown.
    const sumOf = (k) => people.reduce((n, p) => n + num(p[k]), 0);
    const filtered = Boolean(local) || emp !== 'all';
    const cards = filtered
        ? { totalExpenses: sumOf('total'), totalPrepaid: sumOf('prepaid'), totalPostpaid: sumOf('postpaid'), balanceDue: sumOf('balance') }
        : overview || {};
    const due = num(cards.balanceDue);
    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            {error && <Alert severity="error" action={<Button size="small" onClick={() => load(q.trim())}>Retry</Button>} sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2} sx={{ mb: 2.5 }}>
                <Grid size={statSize}><AttStatCard label="Total expenses" value={inr(num(cards.totalExpenses))} icon={PaymentsRoundedIcon} tone="purple" /></Grid>
                <Grid size={statSize}><AttStatCard label="Prepaid" value={inr(num(cards.totalPrepaid))} icon={AccountBalanceWalletRoundedIcon} tone="green" /></Grid>
                <Grid size={statSize}><AttStatCard label="Postpaid" value={inr(num(cards.totalPostpaid))} icon={PendingActionsRoundedIcon} tone="amber" /></Grid>
                <Grid size={statSize}><AttStatCard label={due > 0 ? 'Advance' : 'Balance due'} value={inr(Math.abs(due))} icon={HourglassBottomRoundedIcon} tone="rose" /></Grid>
            </Grid>

            <Box sx={{ ...card, overflow: 'hidden' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 820 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: GRID, bgcolor: '#F7F6FD' }}>
                            {['EMPLOYEE', 'ENTRIES', 'TOTAL', 'PREPAID', 'POSTPAID', 'BALANCE', 'LAST ACTIVITY'].map((h) => <Box key={h} sx={th}>{h}</Box>)}
                        </Box>
                        {loading && !overview ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} /></Box>
                        ) : people.length === 0 ? (
                            <EmptyState icon={GroupsRoundedIcon} title="No payment history yet" hint="Tag employees on a shoot expense and add payments to see them here." />
                        ) : people.map((p) => (
                            <Box key={p.employeeCode} onClick={() => open(p.employeeCode)} sx={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid #EEF0F6', '&:last-of-type': { borderBottom: 'none' }, '&:hover': { bgcolor: '#FAFAFE' } }}>
                                <Box sx={{ ...td, borderBottom: 'none' }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: PRIMARY }} noWrap>{p.employeeName || p.employeeCode}</Typography>
                                    <Typography sx={{ fontSize: 11, color: '#98A0AE' }} noWrap>{p.employeeCode}</Typography>
                                </Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13 }}>{num(p.entries)}</Typography></Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 800 }}>{inr(num(p.total))}</Typography></Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 700, color: '#2F8F5F' }}>{inr(num(p.prepaid))}</Typography></Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, fontWeight: 700, color: '#3B82C4' }}>{inr(num(p.postpaid))}</Typography></Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: balanceTone(p.balanceStatus) }}>{inr(Math.abs(num(p.balance)))}</Typography>
                                    <Typography sx={{ fontSize: 10.5, color: balanceTone(p.balanceStatus), fontWeight: 700 }}>{balanceWord(p.balanceStatus)}</Typography>
                                </Box>
                                <Box sx={{ ...td, borderBottom: 'none' }}><Typography sx={{ fontSize: 13, color: '#64748B' }}>{toDate(p.lastActivity)}</Typography></Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
